/**
 * CardPreviewFront
 *
 * A React recreation of the physical card front shown on the recipient portal.
 * Matches the design language from backend/src/lib/fulfillment/templates.ts —
 * same colours, fonts, corner ornaments, emoji, and occasion label.
 *
 * Rendered at 288×432px (half of the 576×864 print size) so it fits a phone
 * screen without scrolling. Use the `scale` prop to adjust.
 */

interface CardPreviewFrontProps {
  occasionType:       string;
  contactName:        string;
  customOccasionName?: string | null;
}

const OCCASION_CONFIG: Record<string, {
  label:       string;
  emoji:       string;
  accentColor: string;
  accentLight: string;
}> = {
  birthday:        { label: 'Happy Birthday',       emoji: '🎂', accentColor: '#E8C12E', accentLight: '#EBF3EC' },
  anniversary:     { label: 'Happy Anniversary',    emoji: '💍', accentColor: '#B59518', accentLight: '#FBF5E6' },
  christmas:       { label: 'Merry Christmas',      emoji: '🎄', accentColor: '#4C8C6A', accentLight: '#E8F3ED' },
  valentines:      { label: "Happy Valentine's Day",emoji: '💝', accentColor: '#E8A598', accentLight: '#FDF0EE' },
  mothers_day:     { label: "Happy Mother's Day",   emoji: '🌸', accentColor: '#D4849A', accentLight: '#FAF0F3' },
  fathers_day:     { label: "Happy Father's Day",   emoji: '👔', accentColor: '#1A1410', accentLight: '#EEF0F5' },
  graduation:      { label: 'Congratulations!',     emoji: '🎓', accentColor: '#B59518', accentLight: '#FBF5E6' },
  thinking_of_you: { label: 'Thinking of You',      emoji: '💌', accentColor: '#E8C12E', accentLight: '#EBF3EC' },
  custom:          { label: 'With Warm Wishes',     emoji: '✨', accentColor: '#E8C12E', accentLight: '#EBF3EC' },
};

const FALLBACK = { label: 'With Warm Wishes', emoji: '✨', accentColor: '#E8C12E', accentLight: '#EBF3EC' };

const W = 288; // display width  (half of 576px print)
const H = 432; // display height (half of 864px print)

export default function CardPreviewFront({ occasionType, contactName, customOccasionName }: CardPreviewFrontProps) {
  const cfg   = OCCASION_CONFIG[occasionType] ?? FALLBACK;
  const label = occasionType === 'custom' && customOccasionName ? customOccasionName : cfg.label;

  return (
    <div
      style={{
        width:    W,
        height:   H,
        position: 'relative',
        background: '#F8F5EE',
        borderRadius: 8,
        overflow: 'hidden',
        fontFamily: '"Inter", system-ui, sans-serif',
        boxShadow: '0 8px 40px rgba(26,20,16,0.18)',
        flexShrink: 0,
      }}
    >
      {/* Top accent bar */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: cfg.accentColor }} />
      {/* Bottom accent bar */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4, background: cfg.accentColor }} />

      {/* Corner ornaments */}
      {[
        { top: 12, left: 12, borderTop: true,    borderLeft: true,  borderBottom: false, borderRight: false },
        { top: 12, right: 12, borderTop: true,   borderLeft: false, borderBottom: false, borderRight: true  },
        { bottom: 12, left: 12, borderTop: false, borderLeft: true,  borderBottom: true,  borderRight: false },
        { bottom: 12, right: 12, borderTop: false,borderLeft: false, borderBottom: true,  borderRight: true  },
      ].map((corner, i) => (
        <div key={i} style={{
          position: 'absolute',
          width: 16, height: 16,
          top:    corner.top,
          left:   corner.left,
          right:  corner.right,
          bottom: corner.bottom,
          borderTop:    corner.borderTop    ? `1.5px solid ${cfg.accentColor}` : 'none',
          borderLeft:   corner.borderLeft   ? `1.5px solid ${cfg.accentColor}` : 'none',
          borderBottom: corner.borderBottom ? `1.5px solid ${cfg.accentColor}` : 'none',
          borderRight:  corner.borderRight  ? `1.5px solid ${cfg.accentColor}` : 'none',
          opacity: 0.45,
        }} />
      ))}

      {/* Center content */}
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '24px 18px',
      }}>
        {/* Emoji */}
        <div style={{ fontSize: 48, lineHeight: 1, marginBottom: 16 }}>{cfg.emoji}</div>

        {/* Occasion label */}
        <h2 style={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: 20, fontWeight: 600,
          color: '#1A1410', textAlign: 'center',
          lineHeight: 1.2, marginBottom: 10,
        }}>{label}</h2>

        {/* Contact name */}
        <p style={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontStyle: 'italic', fontSize: 13,
          color: cfg.accentColor, textAlign: 'center',
        }}>{contactName}</p>

        {/* Decorative line */}
        <div style={{
          width: 36, height: 1.5,
          background: cfg.accentColor,
          margin: '14px auto', opacity: 0.4,
        }} />

        {/* MomentSent branding */}
        <div style={{
          position: 'absolute', bottom: 12,
          fontFamily: '"Inter", sans-serif',
          fontSize: 7, letterSpacing: '0.12em',
          color: '#C4B9AC', textTransform: 'uppercase',
        }}>MomentSent</div>
      </div>
    </div>
  );
}
